import * as apiValues from '../constants/apiValues';
import { programMapTypes } from '../constants/programsMapValues';

/** URL program slug → iframe map mode / mapContext. */
export const PROGRAM_SLUG_TO_MAP_CONTEXT = {
  'active-transportation': 'active_transportation',
  'great-streets': 'great_streets',
  'people-st': 'people_streets',
  'play-streets': 'play_streets',
  'safe-routes-for-seniors': 'safe_routes_for_seniors',
  'safe-routes-to-school': 'safe_routes_to_schools',
  'vision-zero': 'vision_zero',
};

export function programSlugToMapContext(slug) {
  return PROGRAM_SLUG_TO_MAP_CONTEXT[slug] || null;
}

export function fetchMapConfig() {
  const url = `${apiValues.getCmsPath()}/api/map-config`;
  return fetch(url).then((response) => {
    if (!response.ok) {
      throw new Error(`Failed to load map configuration (${response.status})`);
    }
    return response.json();
  });
}

/** Normalize preset ids so hyphen/underscore variants match legacy map code. */
export function normalizeGroupId(groupId) {
  return (groupId || '').replace(/-/g, '_');
}

function looksLikeTechnicalTabName(name, groupId) {
  const label = (name || '').trim();
  if (!label) return true;
  if (label === groupId) return true;
  if (normalizeGroupId(label) === normalizeGroupId(groupId)) return true;
  // Slug-style ids (no spaces) are not visitor-facing titles.
  return /^[a-z0-9_-]+$/i.test(label) && !/\s/.test(label);
}

export function tabIconTypeFromGroupId(groupId) {
  const id = normalizeGroupId(groupId);
  if (id.includes('memorial_map')) {
    return programMapTypes.memorialMap;
  }
  if (id.endsWith('project_features')) {
    return programMapTypes.projectFeatures;
  }
  if (id.endsWith('projects')) {
    return programMapTypes.projects;
  }
  if (id.endsWith('neighborhoods_zones')) {
    return programMapTypes.neighborhoods;
  }
  if (id.endsWith('transit')) {
    return programMapTypes.transit;
  }
  return programMapTypes.projects;
}

export function tabDisplayName(group) {
  const cmsName = (group.name || '').trim();
  const iconType = tabIconTypeFromGroupId(group.groupId);
  if (looksLikeTechnicalTabName(cmsName, group.groupId)) {
    return iconType.id;
  }
  return cmsName;
}

/** CMS media URLs are relative (/uploads/…); bundled fallbacks are already FE asset URLs. */
export function resolveTabIconSrc(tabIconUrl, fallbackIcon) {
  if (tabIconUrl) {
    return apiValues.imgPath(tabIconUrl);
  }
  return fallbackIcon;
}

/**
 * @param {Array} layerGroups from /api/map-config
 * @param {string} mapContext e.g. overall, vision_zero
 * @param {{ includeIcons?: boolean }} opts
 */
export function getMapTabs(layerGroups, mapContext, opts = {}) {
  const { includeIcons = true } = opts;
  return (layerGroups || [])
    .filter((g) => g.showAsMapTab && g.mapContext === mapContext)
    .sort((a, b) => (a.tabSortOrder ?? 0) - (b.tabSortOrder ?? 0))
    .map((g) => {
      const fallbackType = tabIconTypeFromGroupId(g.groupId);
      const tab = {
        name: tabDisplayName(g),
        id: g.groupId,
        layers: g.layerIds || [],
      };
      if (includeIcons) {
        tab.type = {
          id: tab.name,
          icon: resolveTabIconSrc(g.tabIconUrl, fallbackType.icon),
        };
      }
      return tab;
    });
}

export function getOverallMapTabs(layerGroups) {
  return getMapTabs(layerGroups, 'overall');
}

export function getProgramMapTabs(layerGroups, slug) {
  const mapContext = programSlugToMapContext(slug);
  if (!mapContext) return [];
  return getMapTabs(layerGroups, mapContext);
}
